<template>
  <content-dialog
    v-model="dialogOpen"
    fullscreen-tablet
    card-class="client-card"
    :width="['timeline', 'billing', 'reports', 'appointments'].includes(selectedTab) ? '1100px' : '700px'"
    :min-height="['timeline', 'billing', 'reports', 'appointments'].includes(selectedTab) ? '80vh' : '50vh'"
    max-height="90vh"
    without-blur
    :toolbar-classes="{'text-white': true, 'bg-blue-8': !isArchived, 'bg-red-8': isArchived}"
    :loading="loading"
    :tags="[helpCodes.clients]"
    @hide="closeModal"
  >
    <template #toolbar>
      <q-toolbar>
        <q-btn
          v-close-popup
          dense
          flat
          round
          icon="mdi-close"
        />
        <page-toolbar-title>
          <template v-if="isArchived || (!loading && !canEdit)" #subtitle>
            {{ concatStrIf(' | ', isArchived && 'Archived', !canEdit && 'Read-only') }}
          </template>
          Client
        </page-toolbar-title>
        <help-button />
      </q-toolbar>
      <q-tabs
        v-if="!!client"
        v-model="selectedTab"
        inline-label
        no-caps
        active-color="white"
        narrow-indicator
        dense
        align="left"
      >
        <q-tab
          name="details"
          label="Details"
        />
        <q-tab
          v-if="showBillingTab"
          name="billing"
          label="Billing"
          icon="mdi-cash-multiple"
        />
        <q-tab
          v-if="showReportsTab"
          name="reports"
          label="Reports"
          icon="mdi-file-chart"
        />
        <q-tab
          v-if="showAppointmentsTab"
          name="appointments"
          label="Appointments"
          icon="mdi-calendar"
        />
        <q-tab
          v-if="can(permissions.canAccessClients) || can(permissions.canAccessInvoicing) || can(permissions.canAccessReports)"
          name="timeline"
          label="Timeline"
          icon="mdi-chart-gantt"
        />
      </q-tabs>
    </template>

    <div v-if="!loading && !!client">
      <q-tab-panels
        v-model="selectedTab"
        class="overflow-panels"
      >
        <q-tab-panel
          name="details"
          class="q-pa-none q-pt-md"
        >
          <DetailsPanel
            :client="client"
            :financials="financials"
            :with-quick-links="withQuickLinks"
            :is-archived="isArchived"
            :can-edit="canEdit && !isArchived"
            :with-groups="withGroups"
            :timeline="simpleTimeline"
            @go-to-timeline="selectedTab = 'timeline'"
            @create-appointment="addAppointmentWithClient"
            @create-report="addReportWithClient"
            @create-invoice="addInvoiceWithClient"
            @create-quotation="addQuotationWithClient"
            @reload="loadClient"
          />
        </q-tab-panel>

        <q-tab-panel
          v-if="showBillingTab"
          name="billing"
          class="q-pa-none"
        >
          <BillingPanel
            :client="client"
            @new-invoice="addInvoiceWithClient"
            @new-quotation="addQuotationWithClient"
            @view-quotation="viewingQuotationID = $event"
          />
        </q-tab-panel>

        <q-tab-panel
          v-if="showReportsTab"
          name="reports"
          class="q-pa-none"
        >
          <ReportsPanel
            :client="client"
            @create-report="addReportWithClient"
            @view-report="viewingReportID = $event"
          />
        </q-tab-panel>

        <q-tab-panel
          v-if="showAppointmentsTab"
          name="appointments"
          class="q-pa-none"
        >
          <AppointmentsPanel
            :client="client"
            @new-appointment="addAppointmentWithClient"
            @view-appointment="viewingAppointmentID = $event"
          />
        </q-tab-panel>

        <q-tab-panel
          v-if="can(permissions.canAccessClients) || can(permissions.canAccessInvoicing) || can(permissions.canAccessReports)"
          name="timeline"
          class="q-pa-none"
        >
          <TimelinePanel
            :id="client.id"
            :can-edit="canEdit && !isArchived"
            @add-timeline="(payload) => addTimelineEntry(simpleTimeline, payload)"
            @update-timeline="(payload) => updateTimelineEntry(simpleTimeline, payload)"
            @pin-timeline="(reference, val) => pinTimelineEntry(simpleTimeline, reference, val)"
            @remove-timeline="(payload) => removeTimelineEntry(simpleTimeline, payload)"
          />
        </q-tab-panel>
      </q-tab-panels>
    </div>
    <div v-else-if="!loading">
      <no-results
        icon="mdi-account-off"
        title="Could not find this client"
      />
    </div>

    <template #bottom>
      <q-toolbar v-if="!disableEditing && client && selectedTab === 'details'">
        <q-space />
        <q-btn
          v-if="can(permissions.canEditClients)"
          :color="!isArchived ? 'red-7' : 'green'"
          flat
          :disable="isBusy"
          :label="(isArchived ? 'Restore' : 'Archive')"
          icon-right="mdi-package-down"
          @click="deleteClient"
        />
        <q-btn
          v-if="canEdit && !isArchived"
          color="blue-grey"
          icon-right="mdi-pencil"
          label="Edit"
          :disable="isBusy"
          @click="$refs.editDialog.open(client)"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <ViewAppointmentDialog
    v-if="!!client"
    :id="viewingAppointmentID"
    without-client
    allow-editing
    allow-deleting
    @updated="$bus.emit('calendar:update', {})"
    @deleted="$bus.emit('calendar:update', {})"
    @close="viewingAppointmentID = null"
  />
  <ReportDetailsPartial
    v-if="!!client"
    :id="viewingReportID"
    without-client
    @close="viewingReportID = null"
  />
  <ViewQuotationDialog
    v-if="!!client"
    :id="viewingQuotationID"
    without-client
    without-appointment
    @close="viewingQuotationID = null"
  />

  <EditDialog
    ref="editDialog"
    @reload="loadClient(true)"
  />
</template>

<script>
// const { DateTime: luxon } = require('luxon')
import { defineAsyncComponent } from 'vue'
import { concatStrIf } from 'src/helpers/globals'

import DetailsPanel from './panels/Details'
import TimelinePanel from './panels/Timeline'
import BillingPanel from './panels/Billing'
import ReportsPanel from './panels/Reports'
import AppointmentsPanel from './panels/Appointments'
import EditDialog from './../edit/Dialog'

import ioClientReceiverMixin from 'components/app/clients/partials/view/mixins/ioClientReceiverMixin'
import timelineMergerMixin from 'components/partials/timeline/partials/timelineMergerMixin'

export default {
  components: {
    DetailsPanel,
    TimelinePanel,
    BillingPanel,
    ReportsPanel,
    AppointmentsPanel,
    EditDialog,
    ViewAppointmentDialog: defineAsyncComponent(() => import('../../../calendar/partials/view/Dialog')),
    ReportDetailsPartial: defineAsyncComponent(() => import('../../../reports/partials/view/Dialog')),
    ViewQuotationDialog: defineAsyncComponent(() => import('../../../quotations/partials/view/Dialog'))
  },
  mixins: [ioClientReceiverMixin, timelineMergerMixin],
  props: {
    id: {
      type: Number,
      default: null
    },
    disableEditing: {
      type: Boolean,
      default: false
    },
    withQuickLinks: {
      type: Boolean,
      default: true
    },
    withGroups: {
      type: Boolean,
      default: true
    }
  },
  emits: ['updated', 'archived', 'close'],
  data () {
    return {
      dialogOpen: false,
      loading: false,
      client: null,
      financials: null,
      simpleTimeline: [],
      viewingAppointmentID: null,
      viewingReportID: null,
      viewingQuotationID: null,
      selectedTab: 'details'
    }
  },
  computed: {
    /* Has permission to edit this client */
    canEdit () {
      return this.client && this.client.can_edit
    },
    isArchived () {
      return this.client && this.client.archived
    },
    isSyncing () {
      return this.client && this.client.provider_details && this.client.provider_details.is_syncing
    },
    isBusy () {
      return this.isSyncing
    },
    canViewFinances () {
      return !!this.company.clients_show_financial_summary && this.can(this.permissions.canViewInvoicing)
    },
    showBillingTab () {
      return !!this.client && this.withQuickLinks && this.canViewFinances && (this.can(this.permissions.canAccessInvoicing) || this.can(this.permissions.canAccessQuotations))
    },
    showReportsTab () {
      return !!this.client && this.withQuickLinks && this.can(this.permissions.canAccessReports)
    },
    showAppointmentsTab () {
      return !!this.client && this.withQuickLinks && this.can(this.permissions.canAccessCalendar)
    }
  },
  watch: {
    'id' (val) {
      if (val) {
        this.loadClient()
      }
      else {
        this.dialogOpen = false
      }
    }
  },
  methods: {
    concatStrIf,
    /* Load Client */
    async loadClient (wasEdit) {
      if (this.id === null) { return }
      this.dialogOpen = true
      this.client = null
      this.financials = null
      this.simpleTimeline = []
      this.loading = true

      this.ioListen()
      const { status, payload } = await this.$get('v1/clients', {
        id: this.id
      })
      this.loading = false
      if (status === 'success') {
        this.client = payload.client
        this.financials = payload.financials
        this.simpleTimeline = payload.timeline
        if (wasEdit) {
          this.$emit('updated', this.client)
        }
      }
    },
    /* Delete Client */
    deleteClient () {
      this.$q.dialog({
        title: 'Confirm',
        message: [
          `Are you sure you want to ${this.isArchived ? 'restore' : 'archive'} this client?`,
          !this.isArchived && 'Any documents such as reports and invoices attached to this client may not display the client\'s details unless the client is unarchived.'
        ],
        ok: true,
        cancel: true,
        color: 'orange-7'
      })
        .onOk(async () => {
          this.$loading.show()
          const { status } = await this.$remove('v1/clients', {
            id: this.client.id
          })
          if (status === 'success') {
            this.client.archived = !this.client.archived
            if (this.client.archived) {
              this.$q.notify({
                message: 'Client archived',
                color: 'orange-7',
                textColor: 'black'
              })
            }
            else {
              this.$q.notify({
                message: 'Client restored',
                color: 'green'
              })
            }
            this.$emit('archived', this.client.archived)
            // updated archiving event, index will do the reload instead
            this.$bus.emit('clients:archive', this.client)
          }
          this.$loading.hide()
        })
    },
    closeModal () {
      this.ioDisconnect()
      this.selectedTab = 'details'
      this.$emit('close')
    },
    addInvoiceWithClient () {
      this.goTo('invoicing/add', {
        client_id: this.client.id
      })
    },
    addQuotationWithClient () {
      this.goTo('quotations/add', {
        client_id: this.client.id
      })
    },
    addReportWithClient () {
      this.goTo('reports/add', {
        client_id: this.client.id
      })
    },
    addAppointmentWithClient (times) {
      this.goTo('scheduler', {
        add: true,
        client_id: this.client.id,
        start_date: times ? times.start : null,
        end_date: times ? times.end : null
      })
    }
  },
  mounted () {
    this.loadClient()
  }
}
</script>
